import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { getApiBaseUrl } from "../config";

const AuthContext = createContext();

/** Decodifica o payload do JWT (sem validar assinatura). Deve refletir o que o backend coloca no token. */
function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== "string") {
    return null;
  }
  const parts = jwt.split(".");
  if (parts.length < 2) {
    return null;
  }
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(b64 + pad));
  } catch {
    return null;
  }
}

/**
 * O app usa só `ikaro_user_data` em hasRole / células — não lê o Bearer.
 * Mescla permissões e células do JWT quando o body do login ou /users/me vier vazio.
 */
function mergeUserProfileFromJwt(tokenStr, userData) {
  if (!userData) {
    return userData;
  }
  const payload = decodeJwtPayload(tokenStr);
  if (!payload) {
    return userData;
  }
  const permFromBody =
    Array.isArray(userData.permissions) && userData.permissions.length > 0
      ? userData.permissions
      : null;
  const permFromToken =
    Array.isArray(payload.permissions) && payload.permissions.length > 0
      ? payload.permissions
      : null;
  const prodFromBody =
    Array.isArray(userData.product_permissions) &&
    userData.product_permissions.length > 0
      ? userData.product_permissions
      : null;
  const prodFromToken =
    Array.isArray(payload.product_permissions) &&
    payload.product_permissions.length > 0
      ? payload.product_permissions
      : null;
  const cellsFromBody =
    Array.isArray(userData.cells) && userData.cells.length > 0
      ? userData.cells
      : null;
  const cellsFromToken =
    Array.isArray(payload.cells) && payload.cells.length > 0
      ? payload.cells
      : null;

  return {
    ...userData,
    permissions: permFromBody || permFromToken || userData.permissions || [],
    product_permissions:
      prodFromBody || prodFromToken || userData.product_permissions || [],
    cells: cellsFromBody || cellsFromToken || userData.cells || [],
  };
}

/** Identidade da sessão JWT (sem last_activity/expires) — sliding renewal não deve disparar setState. */
function jwtSessionFingerprint(jwt) {
  if (!jwt || typeof jwt !== "string") {
    return null;
  }
  const parts = jwt.split(".");
  if (parts.length < 2) {
    return null;
  }
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = "=".repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(b64 + pad));
    return [json.user_id, json.email, json.company_id, json.tenant_id].join(
      "|",
    );
  } catch {
    return null;
  }
}

export { decodeJwtPayload, mergeUserProfileFromJwt };

export function useAuth() {
  return useContext(AuthContext);
}

// Hook personalizado para validação de permissões sem loops
export function usePermissionValidation() {
  const { user, hasAnyPermissionWithRules } = useAuth();
  const hasValidated = useRef(false);

  useEffect(() => {
    // Só valida uma vez quando o usuário carrega
    if (hasValidated.current || !user) {
      return;
    }

    hasValidated.current = true;

    // Se não possui permissões com rules, redireciona
    if (!hasAnyPermissionWithRules()) {
      // Limpa o localStorage e redireciona
      localStorage.removeItem("ikaro_auth_token");
      localStorage.removeItem("ikaro_user_data");
      window.location.href = "/login";
    }
  }, [user?.email, user, hasAnyPermissionWithRules]); // Dependências necessárias
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cell, setCell] = useState(null);

  const updateTokenSilently = React.useCallback((newToken) => {
    if (!newToken || typeof newToken !== "string") return;
    localStorage.setItem("ikaro_auth_token", newToken);
    setToken((prev) => {
      if (!prev) {
        return newToken;
      }
      const fPrev = jwtSessionFingerprint(prev);
      const fNew = jwtSessionFingerprint(newToken);
      if (fPrev != null && fNew != null && fPrev === fNew) {
        return prev;
      }
      return newToken;
    });
  }, []);

  // Função para obter a chave de armazenamento específica do usuário e empresa
  const getUserCellKey = (userEmail, companyName) => {
    // Inclui o nome da empresa para evitar conflitos entre empresas
    const sanitizedCompany = companyName
      ? companyName.replace(/[^a-zA-Z0-9]/g, "_")
      : "default";
    return `ikaro_cell_${userEmail}_${sanitizedCompany}`;
  };

  // Função para validar se uma célula existe na lista de células do usuário
  const validateCellInUserCells = (cellToValidate, userCells) => {
    if (!cellToValidate || !userCells || !Array.isArray(userCells)) {
      return null;
    }

    // Verifica se a célula existe na lista
    // userCells pode ser tanto da API (com id/name) quanto já mapeadas (com value/title)
    const foundCell = userCells.find((userCell) => {
      const cellId = userCell.id || userCell.value;
      const cellName = userCell.name || userCell.title;

      return (
        cellId === cellToValidate.value || cellName === cellToValidate.title
      );
    });

    return foundCell ? cellToValidate : null;
  };

  // Função auxiliar para salvar célula no localStorage
  const saveCellToStorage = React.useCallback(
    (userEmail, companyName, cellData) => {
      if (userEmail && cellData) {
        const userCellKey = getUserCellKey(userEmail, companyName);
        localStorage.setItem(userCellKey, JSON.stringify(cellData));
      }
    },
    []
  );

  // Função para carregar a célula específica do usuário e empresa
  const loadUserCell = React.useCallback(
    (userEmail, userCells, companyName, shouldSaveAutoSelection = true) => {
      if (!userEmail || !userCells || !Array.isArray(userCells)) {
        if (userCells && userCells.length > 0) {
          const fallbackCell = {
            title: userCells[0].name || userCells[0].title,
            value: userCells[0].id || userCells[0].value,
          };

          // Salva a seleção automática se solicitado
          if (shouldSaveAutoSelection) {
            saveCellToStorage(userEmail, companyName, fallbackCell);
          }

          return fallbackCell;
        }
        return null;
      }

      const userCellKey = getUserCellKey(userEmail, companyName);
      const storedCell = localStorage.getItem(userCellKey);

      if (storedCell) {
        try {
          const parsedCell = JSON.parse(storedCell);
          const validatedCell = validateCellInUserCells(parsedCell, userCells);

          if (validatedCell) {
            return validatedCell;
          } else {
            // Remove a célula inválida do localStorage
            localStorage.removeItem(userCellKey);
          }
        } catch (error) {
          console.error("Erro ao carregar célula do usuário:", error);
          localStorage.removeItem(userCellKey);
        }
      }

      // Retorna a primeira célula disponível como fallback
      if (userCells && userCells.length > 0) {
        const fallbackCell = {
          title: userCells[0].name || userCells[0].title,
          value: userCells[0].id || userCells[0].value,
        };

        // Salva a seleção automática se solicitado
        if (shouldSaveAutoSelection) {
          saveCellToStorage(userEmail, companyName, fallbackCell);
        }

        return fallbackCell;
      }
      return null;
    },
    [saveCellToStorage]
  );

  useEffect(() => {
    // Carregar o token do localStorage quando o componente for montado
    const storedToken = localStorage.getItem("ikaro_auth_token");
    const storedUser = localStorage.getItem("ikaro_user_data");

    if (storedToken) {
      setToken(storedToken);
    }

    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        const userData = storedToken
          ? mergeUserProfileFromJwt(storedToken, parsed)
          : parsed;
        if (storedToken) {
          localStorage.setItem("ikaro_user_data", JSON.stringify(userData));
        }
        setUser(userData);

        // Carregar célula específica do usuário
        if (userData.email && userData.cells && userData.cells.length > 0) {
          const userCell = loadUserCell(
            userData.email,
            userData.cells,
            userData.company_name
          );
          setCell(userCell);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
        localStorage.removeItem("ikaro_user_data");
      }
    }

    // Limpar células antigas do sistema antigo (sem especificação de usuário)
    const oldCell = localStorage.getItem("ikaro_cell");
    if (oldCell) {
      localStorage.removeItem("ikaro_cell");
    }

    setLoading(false);
  }, [loadUserCell]);

  // Função para buscar células atualizadas da API
  const fetchUserCells = React.useCallback(async () => {
    if (!token) {
      console.warn("Token não disponível para buscar células");
      return null;
    }

    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/cells`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.data || [];
      } else {
        const errorData = await response.json();
        console.error("Erro ao buscar células atualizadas:", errorData);
        return null;
      }
    } catch (error) {
      console.error("Erro ao buscar células atualizadas:", error);
      return null;
    }
  }, [token]);

  const saveAuthData = (tokenData, userData = null) => {
    // Limpar completamente os dados do usuário anterior antes de salvar novos dados
    setCell(null);
    setUser(null);

    localStorage.setItem("ikaro_auth_token", tokenData);
    setToken(tokenData);

    if (userData) {
      const mergedUser = mergeUserProfileFromJwt(tokenData, userData);
      localStorage.setItem("ikaro_user_data", JSON.stringify(mergedUser));
      setUser(mergedUser);

      if (mergedUser.email && mergedUser.cells && mergedUser.cells.length > 0) {
        const userCell = loadUserCell(
          mergedUser.email,
          mergedUser.cells,
          mergedUser.company_name
        );
        setCell(userCell);
        cleanupOtherUsersCells();
      }

      if (mergedUser.email) {
        setTimeout(async () => {
          try {
            const API_BASE_URL = getApiBaseUrl();
            const response = await fetch(`${API_BASE_URL}/users/me`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${tokenData}`,
              },
            });

            if (response.ok) {
              const me = await response.json();
              const updatedUserData = mergeUserProfileFromJwt(tokenData, {
                ...mergedUser,
                permissions:
                  Array.isArray(me.permissions) && me.permissions.length > 0
                    ? me.permissions
                    : mergedUser.permissions,
                product_permissions:
                  Array.isArray(me.product_permissions) &&
                  me.product_permissions.length > 0
                    ? me.product_permissions
                    : mergedUser.product_permissions,
                cells:
                  me.cells && me.cells.length > 0 ? me.cells : mergedUser.cells,
                name: me.name ?? mergedUser.name,
              });
              localStorage.setItem(
                "ikaro_user_data",
                JSON.stringify(updatedUserData)
              );
              setUser(updatedUserData);

              const freshCells = updatedUserData.cells;
              if (freshCells && freshCells.length > 0) {
                const revalidatedCell = loadUserCell(
                  mergedUser.email,
                  freshCells,
                  mergedUser.company_name,
                  false
                );
                if (revalidatedCell) {
                  setCell(revalidatedCell);
                }
              }
            } else {
              console.error(
                "Erro ao buscar dados do usuário após login:",
                await response.json().catch(() => null)
              );
            }
          } catch (error) {
            console.error("Erro ao buscar dados do usuário após login:", error);
          }
        }, 100);
      }
    }
  };

  const clearAuth = React.useCallback(() => {
    localStorage.removeItem("ikaro_auth_token");
    localStorage.removeItem("ikaro_user_data");

    // Limpar completamente o estado quando faz logout
    setToken("");
    setUser(null);
    setCell(null);
  }, []);

  useEffect(() => {
    window.__IKARO_AUTH_UPDATE_TOKEN__ = updateTokenSilently;
    window.__IKARO_AUTH_LOGOUT__ = () => {
      clearAuth();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    };
    return () => {
      try {
        delete window.__IKARO_AUTH_UPDATE_TOKEN__;
        delete window.__IKARO_AUTH_LOGOUT__;
      } catch {
        window.__IKARO_AUTH_UPDATE_TOKEN__ = undefined;
        window.__IKARO_AUTH_LOGOUT__ = undefined;
      }
    };
  }, [updateTokenSilently, clearAuth]);

  const isAuthenticated = () => {
    return !!token;
  };

  const changeCell = (newCell) => {
    if (!user || !user.email) {
      console.warn("Não é possível salvar célula sem usuário autenticado");
      return;
    }

    const userCellKey = getUserCellKey(user.email, user.company_name);
    const currentStoredCell = localStorage.getItem(userCellKey);

    // Verifica se a célula é diferente da atual
    if (currentStoredCell) {
      try {
        const parsedCurrentCell = JSON.parse(currentStoredCell);
        if (JSON.stringify(parsedCurrentCell) === JSON.stringify(newCell)) {
          return; // Não altera se for a mesma célula
        }
      } catch (error) {
        console.error("Erro ao comparar células:", error);
      }
    }

    setCell(newCell);

    if (newCell) {
      localStorage.setItem(userCellKey, JSON.stringify(newCell));
    } else {
      localStorage.removeItem(userCellKey);
    }
  };

  const updateCellName = React.useCallback(
    (cellId, newName) => {
      if (!cellId || typeof newName !== "string") return;

      const trimmed = newName.trim();
      if (!trimmed) return;

      // Atualiza célula selecionada (header)
      setCell((prev) => {
        if (!prev || prev.value !== cellId) return prev;
        return { ...prev, title: trimmed };
      });

      // Atualiza lista de células do user (para filtros/listagens que usam user.cells)
      setUser((prev) => {
        if (!prev) return prev;
        const updatedCells = Array.isArray(prev.cells)
          ? prev.cells.map((c) => {
              const id = c.id || c.value;
              if (id !== cellId) return c;
              if (c.id != null) return { ...c, name: trimmed };
              return { ...c, title: trimmed };
            })
          : prev.cells;

        const next = { ...prev, cells: updatedCells };
        try {
          localStorage.setItem("ikaro_user_data", JSON.stringify(next));
        } catch {
          // ignore storage errors
        }

        // Atualiza célula armazenada por usuário/empresa, se for a atual
        try {
          if (prev.email) {
            const sanitizedCompany = prev.company_name
              ? prev.company_name.replace(/[^a-zA-Z0-9]/g, "_")
              : "default";
            const userCellKey = `ikaro_cell_${prev.email}_${sanitizedCompany}`;
            const stored = localStorage.getItem(userCellKey);
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed && parsed.value === cellId) {
                localStorage.setItem(
                  userCellKey,
                  JSON.stringify({ ...parsed, title: trimmed }),
                );
              }
            }
          }
        } catch {
          // ignore storage errors
        }

        return next;
      });
    },
    [],
  );

  const clearCell = () => {
    if (user && user.email) {
      const userCellKey = getUserCellKey(user.email, user.company_name);
      localStorage.removeItem(userCellKey);
    }
    setCell(null);
  };

  // Função para validar e ajustar a célula atual baseada na lista de células do usuário
  const validateAndAdjustCell = (userCells) => {
    if (!user || !user.email || !userCells || !Array.isArray(userCells)) {
      return;
    }

    const validatedCell = validateCellInUserCells(cell, userCells);

    if (!validatedCell && userCells.length > 0) {
      // Se a célula atual não é válida, seleciona a primeira disponível
      const fallbackCell = {
        title: userCells[0].name || userCells[0].title,
        value: userCells[0].id || userCells[0].value,
      };

      // Usar changeCell para garantir que seja salva no localStorage
      changeCell(fallbackCell);
    } else if (!validatedCell) {
      // Se não há células disponíveis, limpa a célula atual
      setCell(null);

      // Remove do localStorage também
      if (user && user.email) {
        const userCellKey = getUserCellKey(user.email, user.company_name);
        localStorage.removeItem(userCellKey);
      }
    }
  };

  // Função para limpar células de outros usuários (manutenção do localStorage)
  const cleanupOtherUsersCells = () => {
    if (!user || !user.email) return;

    const currentUserCellKey = getUserCellKey(user.email, user.company_name);
    const allKeys = Object.keys(localStorage);

    // Remove células de outros usuários se o localStorage estiver muito cheio
    const cellKeys = allKeys.filter(
      (key) => key.startsWith("ikaro_cell_") && key !== currentUserCellKey
    );

    // Mantém apenas as 10 células mais recentes de outros usuários/empresas
    if (cellKeys.length > 10) {
      cellKeys.slice(10).forEach((key) => {
        localStorage.removeItem(key);
      });
    }
  };

  // Função para forçar limpeza de estados de células em componentes
  const clearCellStates = () => {
    // Esta função pode ser chamada por componentes para garantir
    // que não há dados de células de usuários anteriores
    setCell(null);
  };

  const value = {
    token,
    user,
    saveAuthData,
    clearAuth,
    updateTokenSilently,
    isAuthenticated,
    loading,
    // Manter compatibilidade com o código antigo
    apiKey: token,
    cell,
    saveApiKey: saveAuthData,
    clearApiKey: clearAuth,
    changeCell,
    updateCellName,
    clearCell,
    validateAndAdjustCell, // Nova função para validação externa
    loadUserCell, // Expor função para uso em componentes
    cleanupOtherUsersCells, // Função para limpeza do localStorage
    saveCellToStorage, // Função auxiliar para salvar células
    clearCellStates, // Nova função para limpeza de estados
    fetchUserCells, // Nova função para buscar células da API
    hasRole: (service_name, role_code, product_code) => {
      if (!user) return false;

      if (
        product_code &&
        service_name === "DOCUMENT_CREATION" &&
        user.product_permissions
      ) {
        const pp = user.product_permissions.find(
          (p) => p.product_code === product_code
        );
        if (
          pp &&
          pp.rules &&
          pp.rules.some((r) => r.rule_code === role_code)
        ) {
          return true;
        }
      }

      if (!user.permissions) return false;

      const service = user.permissions.find(
        (p) => p.service_name === service_name
      );
      if (!service || !Array.isArray(service.rules)) return false;

      const role = service.rules.find((r) => r.rule_code === role_code);
      return role ? true : false;
    },
    hasAnyPermissionWithRules: () => {
      if (!user) return false;
      if (
        user.product_permissions?.some((p) => p.rules && p.rules.length > 0)
      ) {
        return true;
      }
      if (!user.permissions) return false;
      return user.permissions.some(
        (permission) => permission.rules && permission.rules.length > 0,
      );
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
